import React from 'react';

import styled from '@emotion/styled';
import type { TagProps } from '@rtkwlf/fenrir-react';
import { sizes, Tag, Text, VStack } from '@rtkwlf/fenrir-react';

import { Box } from '@packages/fenrir/components';

import { ViewMore } from '../view-more/view-more';

interface TagsDisplayProps {
  tags: TagProps[];
  title?: string;
  max?: number;
  shouldTruncateTags?: boolean;
}

const StyledTag = styled(Tag)({
  '> span:before, > span:after': {
    // remove before / after of Tag's Text to accommodate truncating text and list items
    content: 'none',
  },
});

const StyledTruncatedTag = styled(StyledTag)({
  '> span': {
    maxWidth: sizes['10rem'],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const getTags = (tags: TagProps[], shouldTruncateTags?: boolean) =>
  tags.map(({ key, ...tag }, index) =>
    shouldTruncateTags ? (
      <StyledTruncatedTag
        key={key ?? index}
        data-testid="truncated-tag"
        {...tag}
      >
        {tag.children}
      </StyledTruncatedTag>
    ) : (
      <StyledTag key={key ?? index} {...tag}>
        {tag.children}
      </StyledTag>
    ),
  );

export const TagsDisplay: React.FC<TagsDisplayProps> = ({
  tags,
  title,
  max,
  shouldTruncateTags,
}) => {
  return (
    <VStack>
      {title && <Text styledAs="body2">{title}</Text>}
      {max !== undefined ? (
        <ViewMore max={max}>{getTags(tags, shouldTruncateTags)}</ViewMore>
      ) : (
        <Box component="ul" display="flex" gap={1} flexWrap="wrap">
          {getTags(tags, shouldTruncateTags).map((tag, index) => (
            <Box key={tag.key ?? index} component="li">
              {tag}
            </Box>
          ))}
        </Box>
      )}
    </VStack>
  );
};
